import { useState, useEffect, useRef } from 'react';
import { useConfigService } from '@thd-nucleus/experience-context';
import { useIndexedDB } from '@thd-olt-global/thd-storage-utils';
import { getInitialMessage } from '../utils/session-utils';
import { useGenerateQuestions } from './useGenerateQuestions';

/**
 * @param { boolean } withPersistence - Flag to check if persistence is desired
 * @param { Object } session
 * @returns { UseMessageHistoryReturn }
 */
export const useMessageHistory = (withPersistence = true, session) => {
  const isInitialSuggestedQuestionsEnabled = useConfigService('magic-apron-initial-suggested-questions-enabled');
  const maBroadcastChannel = useRef(null);
  const isStorageAvailable = useRef(false);
  const wereQuestionsGenerated = useRef(false);
  const initializationLock = useRef(false);
  const [messages, setMessages] = useState([getInitialMessage(session.sessionId)]);
  const [isLoading, setIsLoading] = useState(false);

  /* eslint-disable camelcase */
  const {
    getAll: iDBGetAll_messages,
    add: iDBAdd_message,
    update: iDBUpdate_message,
    cursor: iDBCursor_message,
  } = useIndexedDB('messages');
  /* eslint-enable camelcase */

  const [submitToGenerateQuestions, {
    data: suggestedQuestionsResponse,
    loading: isSuggestedQuestionsLoading,
    error: suggestedQuestionsError,
  }] = useGenerateQuestions(session);

  const loadIDBMessages = () => {
    setIsLoading(true);
    return iDBGetAll_messages()
      .then((savedMessages) => {
        const sortedMessages = [...savedMessages].sort((first, second) => {
          if (first.isInitialMessage) return -1;
          if (second.isInitialMessage) return 1;
          return 0;
        });
        setMessages(sortedMessages);
        setIsLoading(false);
        return savedMessages;
      });
  };

  const saveMessage = async (newMessage) => {
    const updatedMessage = {
      ...newMessage,
      sessionId: session.sessionId,
      feedbackProvided: false,
      timestamp: Date.now(),
    };
    if (!isStorageAvailable.current) {
      setMessages([...messages, updatedMessage]);
      return Promise.resolve(updatedMessage);
    }
    if (!session.sessionId) return Promise.reject(new Error('Session ID not available'));

    return iDBAdd_message(updatedMessage)
      .then(loadIDBMessages)
      .then((savedMessages) => savedMessages.at(-1));
  };

  // Debounce function to limit the frequency of clearHistory calls
  const debounce = (func, delay) => {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => func.apply(this, args), delay);
    };
  };

  const clearMessageHistory = () => {
    iDBCursor_message((event) => {
      const cursor = event.target.result;
      if (cursor) {
        if (!cursor.value.isInitialMessage) {
          cursor.delete();
        }
        cursor.continue();
      }
    }).then(loadIDBMessages);
  };

  const clearHistory = debounce(clearMessageHistory, 50); // 50ms delay for consistency

  const setFeedbackProvided = async (message) => {
    const updatedMessage = JSON.parse(JSON.stringify(message));
    updatedMessage.feedbackProvided = true;

    await iDBUpdate_message(updatedMessage);
  };

  const broadCastMessageUpdate = () => {
    if (maBroadcastChannel.current) {
      maBroadcastChannel.current.postMessage('magic-apron-new-message');
    }
  };

  const cleanIndexDb = async () => {
    let initialMessageFound = false;
    // Remove messages that have no session associated in db
    await iDBCursor_message((event) => {
      const cursor = event.target.result;

      if (cursor) {
        const isDuplicateInitial = cursor.value.isInitialMessage && initialMessageFound;
        if (cursor.value.isInitialMessage && !initialMessageFound) {
          initialMessageFound = true;
        }
        if ((cursor.value.sessionId !== session.sessionId) || isDuplicateInitial) {
          cursor.delete();
        }
        cursor.continue();
      }
    });
  };

  const init = async () => {
    // Prevent multiple simultaneous initializations
    if (initializationLock.current) return;
    initializationLock.current = true;
    try {
      isStorageAvailable.current = withPersistence && typeof indexedDB !== 'undefined';
      await cleanIndexDb();

      const savedMessages = await iDBGetAll_messages();
      if (!savedMessages.find(({ isInitialMessage }) => isInitialMessage)) {
        await iDBAdd_message(getInitialMessage(session.sessionId))
          .then(() => {
            if (isInitialSuggestedQuestionsEnabled) {
              submitToGenerateQuestions();
            }
          });
      }

      wereQuestionsGenerated.current = true;
      if (typeof window !== 'undefined' && window.parent.LIFE_CYCLE_EVENT_BUS) {
        window.parent.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('magic-apron-assistant.isReady', { isReady: true });
      }

      await loadIDBMessages();
    } finally {
      initializationLock.current = false;
    }
  };

  useEffect(() => {
    if (typeof BroadcastChannel !== 'undefined') {
      maBroadcastChannel.current = new BroadcastChannel('magic-apron-new-message');
    }

    init()
      .then(() => {
        if (maBroadcastChannel.current) {
          maBroadcastChannel.current.onmessage = loadIDBMessages;
        }
      })
      .catch(() => { });

    return (() => {
      if (maBroadcastChannel.current) {
        maBroadcastChannel.current.close();
      }
      // Reset initialization lock on unmount
      initializationLock.current = false;
    });
  }, []);

  useEffect(() => {
    async function commitInitialSuggestedQuestions() {
      if (!isSuggestedQuestionsLoading && !suggestedQuestionsError && suggestedQuestionsResponse) {
        const dbMessages = await iDBGetAll_messages();
        const initialMessage = dbMessages.find(({ isInitialMessage }) => isInitialMessage);

        const generatedSuggestedQuestions = suggestedQuestionsResponse?.generateQuestions?.suggestedQuestions || [];
        initialMessage.content.suggestedQuestions = generatedSuggestedQuestions;

        await iDBUpdate_message(initialMessage)
          .then(loadIDBMessages)
          .finally(() => { broadCastMessageUpdate(); });
      }
    }

    commitInitialSuggestedQuestions().catch(() => {});
  }, [isSuggestedQuestionsLoading, suggestedQuestionsResponse]);

  return {
    messages,
    saveMessage,
    historyLoading: isLoading,
    isSuggestedQuestionsLoading,
    wereQuestionsGenerated: wereQuestionsGenerated.current,
    clearHistory,
    setFeedbackProvided,
    broadCastMessageUpdate,
  };
};
